/* eslint-disable no-nested-ternary */

import React, { useState, useCallback, useContext, useEffect } from 'react';
import { TableCell, TableRow, Tooltip, Checkbox } from '@material-ui/core';
import CancelIcon from '@material-ui/icons/Cancel';
import { withStyles } from '@material-ui/core/styles';
import styled from 'styled-components';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import { STRING_TRUE, STRING_FALSE } from '@mummssoftware/utils/constants';
import { orderingPhysicianLabel } from '@mummssoftware/utils/medications/utils';
import { formatDateMMDDYY } from '@mummssoftware/utils/formatters';
import { parseDateMMDDYYYYSlashes } from '@mummssoftware/utils/dates';
import { PNGlobalContext } from '@mummssoftware/global-context';
import { SuggestBox } from '@mummssoftware/common-ui';
import { FormattedMessage } from 'react-intl';
import { messages } from '../Medication/messages';
import { CoveredDialog } from '../Medication/CoveredDialog';
import { AppContext } from '../../App/AppContext';
import {
  isNeedsApprovalMed,
  showMedsOrderIcon,
  showStartReason,
} from '../../utils/utils';
import {
  getCoveredTooltipTitle,
  getCoveredPlaceholder,
  coveredOptions,
} from '../Medication/constants';
import PrintMedicationButton from '../Print/PrintMedicationButton';
import { MedPhysicianPicker } from '../common/MedPhysicianPicker';
import { StartReasonPopover } from '../Medication/StartReasonPopover';
import { HighRiskToggle } from '../Medication/HighRiskToggle';

import { ADD, REMOVE } from '../../App/constants';

const StyledTableCell = styled(TableCell)`
  && {
    color: inherit;
    white-space: nowrap;
    ${(props) =>
      props.smallwidget
        ? `padding: 2px;
           font-size: 14px;`
        : `padding: 1px;
           font-size: 15px;`}
  }
`;
const StyledTruncateTableCell = styled(TableCell)`
  && {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 1px;
    ${(props) =>
      props.smallwidget
        ? `padding: 2px;
           font-size: 14px;`
        : `padding: 3px;
           font-size: 15px;`}
    color: inherit;
  }
`;

const SmallDiv = styled.div`
  width: 40px;
`;

const BlankIconDiv = styled.div`
  min-width: 25px;
`;

const BlankDateDiv = styled.div`
  min-width: 60px;
`;

const ToggleDiv = styled.div`
  ${(props) =>
    props.isMobile && !props.isMobileEditable ? `;` : `cursor: pointer;`}
  width: 40px;
  display: flex;
  justify-content: center;
`;

const StyledTooltip = withStyles({
  tooltip: {
    color: 'white',
    backgroundColor: 'gray',
    fontSize: '1em',
  },
})(Tooltip);

const BaseMedication = ({
  classes,
  medication,
  handleChange,
  showCheckmarks,
  updateApprovedMedsHash,
}) => {
  const { progressNotesGlobal } = useContext(PNGlobalContext);

  const [openCoveredDialog, setOpenCoveredDialog] = useState(false);

  const coveredError = medication.coveredbyhospice === '';

  const { patient, isInMedOrdersBinList, rcopiaConfig } = useContext(
    AppContext,
  );

  const { hidecovered } = rcopiaConfig;

  const handleCloseCoveredDialog = () => {
    setOpenCoveredDialog(false);
  };

  const checkDiscontinued = useCallback(() => {
    if (medication.stopped) {
      return classes.medInfoValue;
    }
    return '';
  }, [medication.stopped, classes.medInfoValue]);

  const { isMobile } = useContext(AppContext);
  const readonly = true;
  const isMobileEditable = false;

  const [checked, setChecked] = useState(false);
  const [disableCheckcheck, setDisableCheckbox] = useState(false);

  useEffect(() => {
    if (showCheckmarks === false) {
      setChecked(false);
    }
  }, [showCheckmarks]);

  useEffect(() => {
    if (
      !medication.orderingphysicianid ||
      medication.orderingphysicianid === ''
    ) {
      setDisableCheckbox(true);
    } else {
      setDisableCheckbox(false);
    }
  }, [medication.orderingphysicianid]);

  const handleApprovalCheckboxChange = (event) => {
    if (event.target.checked === true) {
      setChecked(true);
      updateApprovedMedsHash(medication, ADD);
    } else {
      setChecked(false);
      updateApprovedMedsHash(medication, REMOVE);
    }
  };

  const medAtAdmit =
    medication &&
    medication.medatadmit &&
    medication.medatadmit.toLowerCase() === STRING_TRUE;

  // TODO:  refactor out approval workflow once all sites are live with med orders.
  // Conditionally show approval ui elements if site is still using the old approval
  // workflow.
  const showNeedsApprovalUI =
    !isInMedOrdersBinList && !showCheckmarks && isNeedsApprovalMed(medication);

  const showApprovalUICheckbox =
    !isInMedOrdersBinList && showCheckmarks && !medAtAdmit;

  const isFromMedOrder = showMedsOrderIcon(medication, progressNotesGlobal);
  const showStartReasonField = showStartReason(medication);

  const showCovered = hidecovered === STRING_FALSE || !hidecovered;

  return (
    <TableRow
      hover
      tabIndex={-1}
      key={medication.sig}
      className={checkDiscontinued()}
    >
      {/* HISTORY MEDICATION PLACEHOLDER */}
      <StyledTableCell>
        <BlankIconDiv />
      </StyledTableCell>
      {/* Progress Note Counts Placeholder */}
      <StyledTableCell>
        <BlankIconDiv />
      </StyledTableCell>
      {/* NEEDS APPROVAL */}
      {showNeedsApprovalUI ? (
        <StyledTooltip title={<FormattedMessage {...messages.needsApproval} />}>
          <StyledTableCell align="center">
            <FiberManualRecordIcon color="error" fontSize="small" />
          </StyledTableCell>
        </StyledTooltip>
      ) : (
        !showCheckmarks && (
          <StyledTableCell>
            <BlankIconDiv />
          </StyledTableCell>
        )
      )}
      {showApprovalUICheckbox ? (
        <StyledTableCell>
          <Checkbox
            checked={checked}
            disabled={disableCheckcheck}
            onChange={(e) => handleApprovalCheckboxChange(e)}
          />
        </StyledTableCell>
      ) : null}
      {/* Non Order Med */}
      <StyledTooltip
        title={<FormattedMessage {...messages.orderColumnToolTipMsg} />}
        aria-label={<FormattedMessage {...messages.orderColumnToolTipMsg} />}
      >
        <StyledTableCell align="center">
          {showMedsOrderIcon(medication, progressNotesGlobal) ? 'Y' : 'N'}
        </StyledTableCell>
      </StyledTooltip>
      {/* MEDICATION NAME */}
      <StyledTooltip
        title={medication.drugdescription}
        aria-label="medication name"
      >
        <StyledTruncateTableCell align="left">
          {medication.drugdescription}
        </StyledTruncateTableCell>
      </StyledTooltip>
      {/* MEDICATION INSTRUCTIONS */}
      <StyledTooltip title={medication.sig}>
        <StyledTruncateTableCell
          align="left"
          aria-label="medication description"
        >
          {medication.sig}
        </StyledTruncateTableCell>
      </StyledTooltip>
      {/* MEDICATION QUANTITY */}
      <StyledTableCell align="center">
        {Math.trunc(medication.quantity)}
      </StyledTableCell>
      {/* MEDICATION STARTDATE */}
      <StyledTableCell align="left">
        {medication.sigchangeddate_shifted
          ? formatDateMMDDYY(
              parseDateMMDDYYYYSlashes(medication.sigchangeddate_shifted),
            )
          : ''}
      </StyledTableCell>
      {/* COVERED BY HOSPICE */}
      {showCovered ? (
        coveredError ? (
          <StyledTableCell>
            <SmallDiv>
              <SuggestBox
                inputId={`covered-${medication.id}`}
                hasError={coveredError}
                fitcontent
                placeholder={getCoveredPlaceholder(medication)}
                tooltipTitle={getCoveredTooltipTitle(medication)}
                options={coveredOptions}
                disabled={readonly}
              />
            </SmallDiv>
            <CoveredDialog
              open={openCoveredDialog}
              onClose={handleCloseCoveredDialog}
              classes={classes}
              medication={medication}
              handleChange={handleChange}
            />
          </StyledTableCell>
        ) : (
          <StyledTableCell>
            <ToggleDiv isMobile={isMobile} isMobileEditable={isMobileEditable}>
              <StyledTooltip
                title={getCoveredTooltipTitle(medication) || ''}
                aria-label="med-covered"
              >
                <div>{getCoveredPlaceholder(medication)}</div>
              </StyledTooltip>
            </ToggleDiv>
            <CoveredDialog
              open={openCoveredDialog}
              onClose={handleCloseCoveredDialog}
              classes={classes}
              medication={medication}
              handleChange={handleChange}
            />
          </StyledTableCell>
        )
      ) : null}
      {/* PHYSICIAN */}
      <MedPhysicianPicker
        medication={medication}
        handleChange={handleChange}
        isFromMedOrder={isFromMedOrder}
      />
      {/* MEDICATION STOPDATE */}
      {medication.stopdate_shifted ? (
        <StyledTooltip title={medication.stopreason} aria-label="stop reason">
          <StyledTableCell align="center">
            {formatDateMMDDYY(
              parseDateMMDDYYYYSlashes(medication.stopdate_shifted),
            )}
          </StyledTableCell>
        </StyledTooltip>
      ) : (
        <StyledTableCell>
          <BlankDateDiv />
        </StyledTableCell>
      )}
      {/* STOP REASON */}
      <StyledTableCell align="center">
        {medication.stopreason ? (
          <StyledTooltip title={medication.stopreason} aria-label="stop reason">
            <span>
              <CancelIcon
                className={classes.discontinuedIcon}
                fontSize="small"
              />
            </span>
          </StyledTooltip>
        ) : (
          <BlankIconDiv />
        )}
      </StyledTableCell>
      {/* HIGH RISK INDICATOR */}
      <StyledTableCell align="center">
        {showStartReasonField ? (
          <HighRiskToggle
            medication={medication}
            handleChange={handleChange}
            readonly
          />
        ) : (
          <BlankIconDiv />
        )}
      </StyledTableCell>
      {/* START REASON */}
      <StyledTableCell align="center">
        {showStartReasonField ? (
          <StartReasonPopover
            medication={medication}
            handleChange={handleChange}
            readonly
          />
        ) : (
          <BlankIconDiv />
        )}
      </StyledTableCell>

      {/* PRINT MEDICATION PLACEHOLDER */}
      <StyledTableCell align="center">
        <PrintMedicationButton
          patient={patient}
          medication={medication}
          orderingPhysicianLabel={orderingPhysicianLabel}
        />
      </StyledTableCell>
    </TableRow>
  );
};

export const HistoryMedication = withStyles((theme) => ({
  overrides: {
    MuiTableCell: {
      root: {
        paddingTop: '6px',
      },
      paddingDefault: {
        padding: '6px',
      },
    },
  },
  hide: {
    display: 'none',
  },
  medInfo: {
    display: 'block',
    color: theme.palette.text.hint,
  },
  medInfoValue: {
    color: theme.palette.text.secondary,
  },
  discontinuedIcon: {
    color: theme.palette.text.secondary,
  },
  error: {
    color: theme.palette.error.main,
    fontWeight: '700',
  },
}))(React.memo(BaseMedication));
